// import ToggleSwitch from "../ToggleSwitch";
import { useState, useRef, useEffect, useCallback } from "react";
import { IoMdClose } from "react-icons/io";
import { FaTag } from "react-icons/fa";
import { BiCoinStack } from "react-icons/bi";
import { FaPercent } from "react-icons/fa";

import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { formatMoneyForInput } from "@/utils/helpers";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";
import CouponLimitInput from "./CouponLimitInput";

type AddCouponProps = {
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

function AddCoupon({ setNeedRefetch }: AddCouponProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  useLockBodyScroll(isModalOpen);
  const [, setPercentage] = useState<number>(1);
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const [isAmountFocused, setIsAmountFocused] = useState(false);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (formik.values.type === "percentage") {
      if (Number(formik.values.amount) > 100) {
        formik.setFieldValue("amount", 100);
      }
      if (Number(formik.values.amount) <= 0) {
        formik.setFieldValue("amount", "");
      }
    }
    const { value } = e.target;
    const numericValue = Number(value);

    if (formik.values.type === "percentage") {
      if (numericValue > 100) {
        setPercentage(100);
        formik.setFieldValue("amount", 100);
      } else if (numericValue <= 0) {
        setPercentage(0);
        formik.setFieldValue("amount", "");
      } else {
        setPercentage(numericValue);
        formik.setFieldValue("amount", numericValue);
      }
    } else {
      formik.handleChange(e);
    }
  };

  const validationSchema = Yup.object({
    code: Yup.string()
      .matches(
        /^[A-Za-z0-9]+$/,
        t(
          "couponAdd.codeValidation"
        ) as string,
      )
      .required(t(
        "couponAdd.codeRequired"
      ) as string),

    startDate: Yup.date()
      .min(
        new Date().toISOString().split("T")[0],
        t("couponAdd.startDateInPast") as string,
      )
      .required(t(
        "couponAdd.startDateRequired"
      ) as string),

    endDate: Yup.date()
      .required(t(
        "couponAdd.endDateRequired"
      ) as string)
      .min(
        Yup.ref("startDate"),
        t(
          "couponAdd.endDateAfterStart"
        ) as string,
      ),

      limit: Yup.string()
      .required(t("coupons.limitsRequired"))
      .min(1, t("coupons.limitsPositive"))
      .test('not-zero', t("coupons.limitsPositive"), value => value !== "0"),

      limitPerUser: Yup.number()
    .required(t("coupons.limitPerUserRequired"))
    .positive(t("coupons.limitPerUserPositive"))
    .test(
      'limitPerUser-not-greater-than-limit',
      t("coupons.limitPerUserExceedslimit"),
      function (value) {
        const { limit } = this.parent;
        if(limit === "infinite") return true
        return limit ? value <= limit : true
      }
    ),



    amount: Yup.number()
      .required(t(
        "couponAdd.amountRequired"
      ) as string)
      .positive(
        t(
          "couponAdd.amountPositive"
        ) as string,
      ),

    type: Yup.string()
      .oneOf(
        ["fixed", "percentage"],
        t(
          "couponAdd.typeInvalid"
        ) as string,
      )
      .required(t("couponAdd.typeRequired") as string),
  });

  const formik = useFormik({
    initialValues: {
      code: "",
      startDate: "",
      endDate: "",
      limit : "",
      limitPerUser: "",
      amount: "",
      type: "fixed",
      // isActive: true,
    },
    validationSchema,
    onSubmit: (values) => {
      const { code, startDate, endDate, amount, type,limitPerUser,limit } = values;
      // date in seconds
      const transformedStartDate = Math.floor(new Date(startDate).getTime() / 1000);
      const transformedEndDate = Math.floor(new Date(endDate).getTime() / 1000);

      const newCoupon = {
        code,
        startDate: transformedStartDate,
        endDate: transformedEndDate,
        limit,
        limitPerUser,
        amount,
        type,
        // active:isActive
      };

      async function addCoupon() {
        const LoadingToast = toast.loading(
          t("couponAdd.creating") as string,
        );
        try {
          const response = await axios.post(
            `${BASE_URL}/api/dashboard/coupons?locale=${lang}`,
            newCoupon,
            {
              headers: {
                Authorization: token,
              },
            },
          );

          if (!response.data.success) {
            toast.error(
              response.data.error ||
              (t("couponAdd.createFailed") as string),
            );
            return;
          }
          toast.success(
            t("couponAdd.createSuccess") as string,
          );
          setIsModalOpen(false);
          formik.resetForm();
          setNeedRefetch(true);
        } catch (error) {
          if (axios.isAxiosError(error)) {
            const errorMessage =
              error.response?.data?.error ||
              (t("couponAdd.createFailed") as string);
            toast.error(errorMessage);
          } else {
            toast.error(
              t("couponAdd.unexpectedError") as string,
            );
          }
          console.error(
            t("couponAdd.createError") as string,
          );
        } finally {
          toast.dismiss(LoadingToast);
        }
      }

      addCoupon();
    },
  });

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setIsModalOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleOutsideClick, isModalOpen]);

  return (
    <div>
      <button
        onClick={() => setIsModalOpen(true)}
        className="self-end rounded-md bg-sidebar px-4 py-2 text-sm font-medium capitalize text-skyblue md:px-5 md:py-2 md:text-base"
      >
        {t("couponAdd.addTitle")}
      </button>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            ref={modalRef}
            className="flex w-[95%] max-w-3xl flex-col gap-7 rounded-lg bg-white px-2 py-3 shadow-lg sm:px-4 sm:py-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm font-semibold sm:text-base md:text-2xl">
                <FaTag className="text-xl" />
                <h2 className="text-lg font-semibold">
                  {t("couponAdd.details")}
                </h2>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="mb-3 text-gray-600 hover:text-gray-800"
              >
                <IoMdClose size={24} className="text-red-600" />
              </button>
            </div>

            <form
              id="add-coupon-form"
              onSubmit={formik.handleSubmit}
              className="thin-scrollbar flex max-h-[55dvh] flex-col gap-6 overflow-y-auto px-1"
            >
              {/* Coupon Code */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700">
                  {t("couponAdd.codeLabel")}
                </label>
                <input
                  type="text"
                  name="code"
                  placeholder={
                    t("couponAdd.codeHint") as string
                  }
                  value={formik.values.code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border"
                />
                {formik.touched.code && formik.errors.code && (
                  <div className="mt-1 text-xs text-red-500">
                    {formik.errors.code}
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="flex gap-3">
                <div className="flex w-full flex-col">
                  <label className="text-sm font-medium text-gray-700">
                    {t("couponAdd.startDate")}
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    min={new Date().toISOString().split("T")[0]}
                    value={formik.values.startDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border"
                  />
                  {formik.touched.startDate && formik.errors.startDate && (
                    <div className="mt-1 text-xs text-red-500">
                      {formik.errors.startDate}
                    </div>
                  )}
                </div>
                <div className="flex w-full flex-col">
                  <label className="text-sm font-medium text-gray-700">
                    {t("couponAdd.endDate")}
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    min={(() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      return tomorrow.toISOString().split("T")[0];
                    })()}
                    value={formik.values.endDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border"
                  />
                  {formik.touched.endDate && formik.errors.endDate && (
                    <div className="mt-1 text-xs text-red-500">
                      {formik.errors.endDate}
                    </div>
                  )}
                </div>
              </div>

              {/* usage Limits */}
              <div className="flex gap-3 w-full ">

                {/* Limit */}
                <div className="flex flex-col w-full items-start">
                    <label className="text-sm font-medium text-gray-700">{t("coupons.limit")}</label>
                    <CouponLimitInput
                      limit={formik.values.limit}
                      onChange={(value) =>
                        formik.setFieldValue("limit", value)
                      }
                    />
                    {formik.touched.limit && formik.errors.limit && (
                      <div className="text-xs mt-1 text-red-500">
                        {formik.errors.limit}
                      </div>
                    )}
                  </div>

              {/* Max User Usage Limit */}
                <div className="flex flex-col w-full items-start">
                  <label className="text-sm font-medium text-gray-700">
                    {t("coupons.maxUserUsage")}
                  </label>
                  <input
                    type="number"
                    name="limitPerUser"
                    placeholder={t("coupons.maxUserUsage")}
                    value={formik.values.limitPerUser}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="mt-1 rounded-md h-11 border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border w-full"
                  />
                  {formik.touched.limitPerUser && formik.errors.limitPerUser && (
                    <div className="mt-1 text-xs text-red-500">
                      {formik.errors.limitPerUser}
                    </div>
                  )}
                </div>
              </div>
            

              {/* Coupon Type */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700">
                  {t("couponAdd.typeLabel")}
                </label>
                <select
                  name="type"
                  value={formik.values.type}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue("amount", "");
                  }}
                  className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border"
                >
                  <option value="fixed">
                    {t("couponAdd.fixed")}
                  </option>
                  <option value="percentage">
                    {t("couponAdd.percentage")}
                  </option>
                </select>
              </div>

              {/* Amount */}
              <div className="relative mb-2 flex flex-col">
                <label className="text-sm font-medium text-gray-700">
                  {t("couponAdd.amountLabel")}
                </label>
                <input
                  type="number"
                  name="amount"
                  placeholder={t("couponAdd.discountAmount") as string}

                  value={isAmountFocused ? formik.values.amount : formatMoneyForInput(formik.values.amount)}
                  onFocus={() => setIsAmountFocused(true)}
                  onBlur={(e) => {
                    setIsAmountFocused(false);
                    formik.handleBlur(e);
                  }}
                  onChange={handleAmountChange}
                  className="mt-1 rounded-md border border-gray-300 p-2 pe-10 focus:outline-none focus:ring-2 focus:ring-border"
                />
                <div className="absolute end-2 top-10 flex items-center text-gray-500">
                  {formik.values.type === "fixed" && <BiCoinStack size={18} />}
                  {formik.values.type === "percentage" && (
                    <FaPercent size={15} />
                  )}
                </div>
                {formik.touched.amount && formik.errors.amount && (
                  <div className="mt-1 text-xs text-red-500">
                    {formik.errors.amount}
                  </div>
                )}
              </div>


            </form>
            {/* Submit */}
            <button
              type="submit"
              form="add-coupon-form"
              className="w-fit self-end rounded-md bg-sidebar px-4 py-2 text-skyblue"
            >
              {t("couponAdd.createButton")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddCoupon;
